import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import {
    ISalesforceOrderDAO,
    ISalesforcePriceBookProductDTO,
} from '../../../../../api/salesforce-order.dao';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { toNumber } from './salesforce-order-display.mapper';
import {
    MANAGE_PRODUCTS_CANCEL_WARNING_ABORT,
    MANAGE_PRODUCTS_CANCEL_WARNING_CONFIRM,
    MANAGE_PRODUCTS_CANCEL_WARNING_MESSAGE,
    MANAGE_PRODUCTS_CANCEL_WARNING_TITLE,
    MANAGE_PRODUCTS_MAX_REACHED_TEXT,
} from './salesforce-order.texts';

export const MAX_PRODUCTS = 200;

interface IManageProductsSelection {
    rowId: string;
    lineItemId: string;
    pricebookEntryId: string;
    productName: string;
    currency: string;
    listPrice: number | null;
    unitPrice: number | null;
    notes: string;
    quantity: number;
    checked: boolean;
}

interface IManageProductsCtorParams {
    contractId: string | null;
    priceBookId: string | null;
    priceBookName: string;
    currency: string;
    initialSelections: IManageProductsSelection[];
    onApply: (selections: IManageProductsSelection[], currency: string) => void;
    onClose: () => void;
}

const includesCI = (haystack: string, needle: string): boolean =>
    haystack.toUpperCase().indexOf(needle.toUpperCase()) !== -1;

export const selectionLineTotal = (selection: IManageProductsSelection): number =>
    (selection.unitPrice ?? 0) * selection.quantity;

export const selectionNeedsNote = (selection: IManageProductsSelection): boolean =>
    (selection.unitPrice ?? 0) !== (selection.listPrice ?? 0) && !selection.notes.trim();

export const selectionHasInvalidUnitPrice = (selection: IManageProductsSelection): boolean =>
    selection.unitPrice == null;

const UNIT_PRICE_PATTERN = /^-?(\d+\.?\d*|\.\d+)$/;

const QUANTITY_PATTERN = /^(\d+(\.\d{0,2})?|\.\d{0,2})$/;

export const isQuantityInput = (value: string): boolean => {
    const raw = value.replace(/,/g, '').trim();
    return raw === '' || QUANTITY_PATTERN.test(raw);
};

const parseUnitPrice = (value: string): number | null => {
    const raw = value.replace(/,/g, '').trim();
    return UNIT_PRICE_PATTERN.test(raw) ? Number(raw) : null;
};

const cloneSelections = (selections: IManageProductsSelection[]): IManageProductsSelection[] =>
    selections.map(selection => ({ ...selection }));

let rowSeq = 0;
export const nextRowId = (): string => `row-${++rowSeq}`;

interface IRowProduct {
    pricebookEntryId: string;
    productName: string;
    currency: string;
    listPrice: number | null;
}

export const makeRow = (
    product: IRowProduct,
    overrides: Partial<IManageProductsSelection> = {},
): IManageProductsSelection => ({
    rowId: nextRowId(),
    lineItemId: '',
    pricebookEntryId: product.pricebookEntryId,
    productName: product.productName,
    currency: product.currency,
    listPrice: product.listPrice,
    unitPrice: product.listPrice,
    notes: '',
    quantity: 0,
    checked: false,
    ...overrides,
});

const rowFromProduct = (product: ISalesforcePriceBookProductDTO): IManageProductsSelection => makeRow({
    pricebookEntryId: product.pricebookEntryId,
    productName: product.productName,
    currency: product.currencyIsoCode,
    listPrice: toNumber(product.listPrice),
});

class ManageProductsViewModel {
    @mobx.observable
    private catalogue: ISalesforcePriceBookProductDTO[] = [];
    @mobx.observable
    private loading: boolean = false;
    @mobx.observable
    private error: boolean = false;
    @mobx.observable
    private searchTerm: string = '';
    @mobx.observable
    private currentCurrency: string;
    @mobx.observable
    private selections: IManageProductsSelection[];

    private requestSeq: number = 0;

    private readonly contractId: string | null;
    private readonly priceBookId: string | null;
    public readonly priceBookName: string;
    // Products already committed to the order (from a previous Apply) — quantities/notes to restore per product.
    private appliedSelections: IManageProductsSelection[];
    private initialSelections: IManageProductsSelection[] = [];
    private readonly onApply: (selections: IManageProductsSelection[], currency: string) => void;
    private readonly onClose: () => void;

    constructor(
        params: IManageProductsCtorParams,
        @IoC.Inject private readonly salesforceOrderDAO: ISalesforceOrderDAO = IoC.Container.get(ISalesforceOrderDAO),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.contractId = params.contractId;
        this.priceBookId = params.priceBookId;
        this.priceBookName = params.priceBookName;
        this.currentCurrency = params.currency;
        this.appliedSelections = cloneSelections(params.initialSelections);
        this.selections = [];
        this.onApply = params.onApply;
        this.onClose = params.onClose;
        this.load();
    }

    private get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    @mobx.action.bound
    public async load(): Promise<void> {
        if (!this.priceBookId) {
            return;
        }
        const seq = ++this.requestSeq;
        this.startLoading();
        try {
            const response = await this.salesforceOrderDAO.getPriceBookProducts({
                priceBookId: this.priceBookId,
                contractId: this.contractId,
            });
            if (seq !== this.requestSeq) {
                return;
            }
            this.setCatalogue(response?.products ?? []);
            this.initRowsFromCatalogue();
        } catch {
            if (seq === this.requestSeq) {
                this.setError();
            }
        } finally {
            if (seq === this.requestSeq) {
                this.stopLoading();
            }
        }
    }

    @mobx.action
    private startLoading(): void {
        this.loading = true;
        this.error = false;
    }

    @mobx.action
    private stopLoading(): void {
        this.loading = false;
    }

    @mobx.action
    private setCatalogue(products: ISalesforcePriceBookProductDTO[]): void {
        this.catalogue = products;
    }

    @mobx.action
    private initRowsFromCatalogue(): void {
        const currentCatalogue = this.catalogue.filter(product => product.currencyIsoCode === this.currentCurrency);
        const catalogueIds = new Set(currentCatalogue.map(product => product.pricebookEntryId));
        const idByName = new Map(currentCatalogue.map(product => [product.productName, product.pricebookEntryId]));

        const appliedByProduct = new Map<string, IManageProductsSelection[]>();
        this.appliedSelections.forEach(selection => {
            const productId = catalogueIds.has(selection.pricebookEntryId)
                ? selection.pricebookEntryId
                : idByName.get(selection.productName);
            if (productId == null) {
                return;
            }
            const group = appliedByProduct.get(productId);
            if (group) {
                group.push(selection);
            } else {
                appliedByProduct.set(productId, [selection]);
            }
        });

        this.selections = currentCatalogue.reduce<IManageProductsSelection[]>((rows, product) => {
            const applied = appliedByProduct.get(product.pricebookEntryId);
            if (!applied || applied.length === 0) {
                rows.push(rowFromProduct(product));
                return rows;
            }
            applied.forEach(selection => rows.push({
                ...selection,
                rowId: selection.rowId || nextRowId(),
                pricebookEntryId: product.pricebookEntryId,
                listPrice: toNumber(product.listPrice),
                checked: true,
            }));
            return rows;
        }, []);
        this.initialSelections = cloneSelections(this.selections);
    }

    @mobx.action
    private setError(): void {
        this.error = true;
        this.catalogue = [];
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public get hasError(): boolean {
        return this.error;
    }

    public get currency(): string {
        return this.currentCurrency;
    }

    public get search(): string {
        return this.searchTerm;
    }

    @mobx.action.bound
    public setSearch(term: string): void {
        this.searchTerm = term;
    }

    /** The rows the table renders — every catalogue product, plus a row per extra line item, filtered by search. */
    public get visibleRows(): IManageProductsSelection[] {
        const term = this.searchTerm.trim();
        return term ? this.selections.filter(row => includesCI(row.productName, term)) : this.selections;
    }

    public get isCatalogueEmpty(): boolean {
        return !this.loading && !this.error && this.visibleRows.length === 0;
    }

    public get selectedProducts(): IManageProductsSelection[] {
        return this.selections;
    }

    /** The rows that will be sent to Salesforce. Unchecking a row is how a line item is removed (AC4). */
    public get orderedProducts(): IManageProductsSelection[] {
        return this.selections.filter(selection => selection.checked && selection.quantity > 0);
    }

    public get selectedCount(): number {
        return this.orderedProducts.length;
    }

    public getRow(rowId: string): IManageProductsSelection | undefined {
        return this.selections.find(selection => selection.rowId === rowId);
    }

    public get total(): number {
        return this.orderedProducts.reduce((sum, selection) => sum + selectionLineTotal(selection), 0);
    }

    @mobx.action
    private updateRow(rowId: string, update: Partial<IManageProductsSelection>): void {
        this.selections = this.selections.map(selection =>
            (selection.rowId === rowId ? { ...selection, ...update } : selection));
    }

    @mobx.action.bound
    public toggleRow(rowId: string): void {
        const row = this.getRow(rowId);
        if (!row) {
            return;
        }
        const checked = !row.checked;
        this.updateRow(rowId, {
            checked: checked,
            quantity: checked && row.quantity === 0 ? 1 : row.quantity,
        });
    }

    @mobx.action.bound
    public addLineItem(rowId: string): void {
        const index = this.selections.findIndex(selection => selection.rowId === rowId);
        if (index === -1) {
            return;
        }
        const source = this.selections[index];
        const added = makeRow(source, { quantity: 1, checked: true });
        this.selections = [
            ...this.selections.slice(0, index + 1),
            added,
            ...this.selections.slice(index + 1),
        ];
    }

    @mobx.action.bound
    public setUnitPrice(rowId: string, value: string): void {
        this.updateRow(rowId, { unitPrice: parseUnitPrice(value) });
    }

    @mobx.action.bound
    public setNotes(rowId: string, value: string): void {
        this.updateRow(rowId, { notes: value });
    }

    @mobx.action.bound
    public setQuantity(rowId: string, value: string): void {
        if (!isQuantityInput(value)) {
            return;
        }
        const raw = value.replace(/,/g, '').trim();
        this.updateRow(rowId, { quantity: raw === '' || raw === '.' ? 0 : Number(raw) });
    }

    @mobx.action.bound
    public commitQuantity(rowId: string): void {
        const row = this.getRow(rowId);
        if (row && row.checked && row.quantity === 0) {
            this.updateRow(rowId, { checked: false });
        }
    }

    public get isDirty(): boolean {
        return JSON.stringify(this.selections) !== JSON.stringify(this.initialSelections);
    }


    public needsNote(rowId: string): boolean {
        const selection = this.getRow(rowId);
        return selection != null && selection.checked && selection.quantity > 0 && selectionNeedsNote(selection);
    }

    public get hasNoteErrors(): boolean {
        return this.orderedProducts.some(selectionNeedsNote);
    }

    public hasInvalidUnitPrice(rowId: string): boolean {
        const selection = this.getRow(rowId);
        return selection != null && selection.checked && selection.quantity > 0 && selectionHasInvalidUnitPrice(selection);
    }

    public get hasUnitPriceErrors(): boolean {
        return this.orderedProducts.some(selectionHasInvalidUnitPrice);
    }

    @mobx.action.bound
    public apply(): void {
        const ordered = this.orderedProducts;
        if (ordered.length > MAX_PRODUCTS) {
            showNotification(this.intl.formatMessage(MANAGE_PRODUCTS_MAX_REACHED_TEXT, { max: MAX_PRODUCTS }));
            return;
        }
        if (this.hasNoteErrors || this.hasUnitPriceErrors) {
            return;
        }
        this.onApply(cloneSelections(ordered), this.currentCurrency);
        this.onClose();
    }

    @mobx.action.bound
    public requestCancel(): void {
        if (!this.isDirty) {
            this.onClose();
            return;
        }
        this.alertsService.confirm({
            title: this.intl.formatMessage(MANAGE_PRODUCTS_CANCEL_WARNING_TITLE),
            message: this.intl.formatMessage(MANAGE_PRODUCTS_CANCEL_WARNING_MESSAGE),
            confirmButtonProps: {
                text: this.intl.formatMessage(MANAGE_PRODUCTS_CANCEL_WARNING_CONFIRM),
                modifier: 'confirm-warn',
                handler: () => this.onClose(),
            },
            cancelButtonProps: {
                text: this.intl.formatMessage(MANAGE_PRODUCTS_CANCEL_WARNING_ABORT),
            },
        });
    }
}

export { ManageProductsViewModel, IManageProductsSelection };
